/**
 * Created: 2026-08-12
 * Initial commit: Add role selection dialog for multi-role users
 *
 * Shown after QR login when the user has more than one loginable role —
 * e.g. app admin AND admin of one or more sites, or admin of multiple sites.
 */

import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import CloseIcon from '@mui/icons-material/Close'
import {
  Avatar,
  Dialog,
  DialogTitle,
  IconButton,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
} from '@mui/material'
import { getImageUrl } from '../config'
import { SiteRole } from '../gql/graphql'
import type { SiteInfo } from '../hooks'

export type SelectedRole =
  | { type: 'app_admin' }
  | { type: 'site_admin'; site: SiteInfo }

interface RoleSelectionDialogProps {
  isAppAdmin: boolean
  adminSites: SiteInfo[]
  currentRole?: SelectedRole
  onSelect: (role: SelectedRole) => void
  onClose?: () => void
}

export default function RoleSelectionDialog({
  isAppAdmin,
  adminSites,
  currentRole,
  onSelect,
  onClose,
}: RoleSelectionDialogProps) {
  const isCurrentRole = (role: SelectedRole): boolean => {
    if (!currentRole) return false
    if (role.type === 'app_admin' && currentRole.type === 'app_admin') return true
    if (
      role.type === 'site_admin' &&
      currentRole.type === 'site_admin' &&
      role.site.id === currentRole.site.id
    ) {
      return true
    }
    return false
  }

  const showAppAdmin = isAppAdmin && !isCurrentRole({ type: 'app_admin' })
  const availableSites = adminSites.filter(
    (site) => !isCurrentRole({ type: 'site_admin', site }),
  )

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        选择登录身份
        {onClose && (
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{ marginLeft: 'auto', marginRight: -1 }}
          >
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <List disablePadding sx={{ pb: 2 }}>
        {showAppAdmin && (
          <ListItem disablePadding>
            <ListItemButton onClick={() => onSelect({ type: 'app_admin' })}>
              <ListItemAvatar>
                <Avatar>
                  <AdminPanelSettingsIcon />
                </Avatar>
              </ListItemAvatar>
              <ListItemText primary="平台管理员" secondary="管理所有场馆和用户" />
            </ListItemButton>
          </ListItem>
        )}
        {availableSites.map((site) => (
          <ListItem key={site.id} disablePadding>
            <ListItemButton onClick={() => onSelect({ type: 'site_admin', site })}>
              <ListItemAvatar>
                <Avatar
                  src={site.avatar ? getImageUrl(site.avatar) : undefined}
                  alt={site.name}
                >
                  {site.name.charAt(0)}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={site.name}
                secondary={site.role === SiteRole.SuperAdmin ? '超级管理员' : '管理员'}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Dialog>
  )
}
